// Search Types and Interfaces for 1vX.gg

export type SearchResultType = 'article' | 'team' | 'player' | 'tournament' | 'match';

// Shared nested shapes. These mirror the (nullable) GraphQL response fields so
// they can be reused both by the raw query result types and by SearchResult,
// which keeps the search-service transform assignable without casts.
export interface SearchAsset {
  url?: string | null;
  title?: string | null;
  description?: string | null;
}

export interface SearchGameRef {
  id?: string | null;
  name?: string | null;
  image?: SearchAsset | null;
}

export interface SearchTeamRef {
  id?: string | null;
  name?: string | null;
  slug?: string | null;
  logo?: SearchAsset | null;
}

export interface SearchPlayerRef {
  id?: string | null;
  gamertag?: string | null;
  slug?: string | null;
}

export interface SearchFilters {
  contentType?: SearchResultType[];
  dateRange?: {
    from?: string;
    to?: string;
  };
  author?: string[];
  game?: string[];
  tags?: string[];
}

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  excerpt: string;
  url: string;
  publishedDate?: string | null;
  author?: string;
  tags: string[];
  relevanceScore: number;
  highlightedFields: Record<string, string>;

  // Type-specific fields
  // For articles
  category?: 'news' | 'review' | 'esports';
  coverImage?: SearchAsset | null;

  // For teams
  country?: string | null;
  region?: string | null;
  game?: SearchGameRef | null;
  players?: SearchPlayerRef[] | null;
  achievements?: string[];
  logo?: SearchAsset | null;

  // For players
  gamertag?: string | null;
  realName?: string | null;
  team?: string;
  role?: string | null;
  photo?: SearchAsset | null;
  currentTeam?: SearchTeamRef | null;

  // For tournaments
  startDate?: string | null;
  endDate?: string | null;
  format?: string | null;
  prizePool?: number | null;
  image?: SearchAsset | null;

  // For matches
  teams?: SearchTeamRef[] | null;
}

export interface SearchResponse {
  query: string;
  total: number;
  page: number;
  limit: number;
  results: SearchResult[];
  suggestions?: string[];
  filters: SearchFilters;
  executionTime?: number;
}

export interface SearchSuggestion {
  text: string;
  type?: SearchResultType;
  count?: number;
}

export interface SearchRequest {
  query: string;
  filters?: SearchFilters;
  page?: number;
  limit?: number;
  includeHighlights?: boolean;
  includeSuggestions?: boolean;
}

// Database types for search index
export interface SearchIndexRecord {
  id: string;
  content_type: SearchResultType;
  content_id: string;
  title: string;
  content: string;
  excerpt?: string;
  url: string;
  published_date?: string;
  author?: string;
  tags: string[];
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Analytics event types
export interface SearchAnalytics {
  search_performed: {
    query: string;
    results_count: number;
    execution_time?: number;
    content_type_filter?: string;
    user_id?: string;
    session_id?: string;
  };

  search_result_clicked: {
    query: string;
    result_id: string;
    result_type: SearchResultType;
    result_title: string;
    position: number;
    user_id?: string;
    session_id?: string;
  };

  search_no_results: {
    query: string;
    content_type_filter?: string;
    user_id?: string;
    session_id?: string;
  };

  search_suggestion_clicked: {
    suggestion: string;
    original_query: string;
    user_id?: string;
    session_id?: string;
  };
}

// Service method options
export interface SearchOptions {
  enableAnalytics?: boolean;
  enableCache?: boolean;
  cacheTimeout?: number;
  userId?: string;
  sessionId?: string;
}

// Error types
export class SearchError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
  ) {
    super(message);
    this.name = 'SearchError';
  }
}

export class SearchValidationError extends SearchError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR', 400);
  }
}

export class SearchServiceError extends SearchError {
  constructor(message: string) {
    super(message, 'SERVICE_ERROR', 500);
  }
}
